/*
 * ew-use-case-prompt.js — Universal use-case capture (capture-v2)
 *
 * The livery-v1 spec says no path should set a car without a use
 * case. The homepage capture section already prompts. This file
 * extends that contract to EVERY vehicle-entry point in the theme:
 * the header garage pill, the Path Finder vehicle button, the Garage
 * pages, the megamenu vehicle entry, the shop-modes "set vehicle"
 * tile, etc. Any path that fires `ew:vehicle-resolved` now triggers
 * the global use-case modal if no use case is set yet.
 *
 * Behavior
 *   - Listens for `ew:vehicle-resolved` document event.
 *   - If `EWLivery.readUseCase()` is null AND we're not on the
 *     homepage capture section (which handles its own prompting
 *     inline), open the modal.
 *   - Listens for `ew:use-case-changed` and closes the modal on tap.
 *   - Paints chassis pre-guess flag + Drift-prominent flag + latched
 *     selection on every render — both inline (homepage capture) and
 *     modal contexts. Same code path; the row is the same snippet.
 *   - Reads/writes through window.EWLivery (no parallel state).
 *
 * Public API
 *   window.EWUseCasePrompt.open(reason?)   — show the modal
 *   window.EWUseCasePrompt.close()          — hide
 *   window.EWUseCasePrompt.paint(vehicle?)  — repaint pre-guess +
 *                                              latched state across
 *                                              every use-case row on
 *                                              the page
 */
(function () {
  'use strict';

  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }

  /* Dismissal-persistence key (G-A7). Once the visitor dismisses the
     use-case prompt — via "keep it neutral", the × / scrim close, OR by
     choosing a card — we set this flag so the modal does NOT auto-pop
     again on subsequent pages/navigations. Same raw-string +
     try/catch localStorage convention as the resolver/livery code
     (ew_active_vehicle, ew_use_case). The header override pill still
     calls open() directly, so dismissal never removes the ability to
     re-open on demand. */
  var LS_UC_DISMISSED = 'ew_uc_dismissed';
  function readDismissed() {
    try { return localStorage.getItem(LS_UC_DISMISSED) === '1'; }
    catch (_) { return false; }
  }
  function markDismissed() {
    try { localStorage.setItem(LS_UC_DISMISSED, '1'); } catch (_) {}
  }

  function readVehicle() {
    try { return JSON.parse(localStorage.getItem('ew_active_vehicle') || 'null'); }
    catch (_) { return null; }
  }
  function chassisOf(v)  { return v ? String(v.code || v.chassis_code || v.chassis || '').toUpperCase().trim() : ''; }
  function engineOf(v)   { return v ? String(v.engine_code  || v.engine  || '').toUpperCase().trim() : ''; }
  /* R2 2.1 — was the drifted copy: it had NO ENGINE_ALIAS, so a B46 car keyed
     EWLivery.PREGUESS_MAP as 'B46:G20' (absent) and fell through to the
     'street' default. 38 of 162 chassis rows carry B46/N26. Now the one shared
     implementation in assets/ew-engine-family.js. */
  var engineFamily = window.EWEngineFamily;
  function vehLabel(v) {
    if (!v) return 'your vehicle';
    /* Phase 1 Follow-up C — eyebrow format: [{CHASSIS}] {MAKE} {MODEL}
       {TRIM} (no year; chassis bracketed; trim conditionally rendered;
       no trailing space when trim absent). The .ew-overline class on
       the capture eyebrow + .toUpperCase() on the modal slot uppercase
       the visible text — vehLabel returns mixed case as-stored.
       Defensive fallback: if chassis + make + model all missing
       (legacy partial-write state), fall back to v.label or the
       generic placeholder so the eyebrow never goes blank. */
    var chassis = String(v.code || v.chassis || v.chassis_code || '').toUpperCase();
    var make = v.make || '';
    var model = v.model || '';
    var trim = v.trim || '';
    if (!chassis && !make && !model) {
      return v.label || 'your vehicle';
    }
    var parts = [];
    if (chassis) parts.push('[' + chassis + ']');
    if (make) parts.push(make);
    if (model) parts.push(model);
    if (trim) parts.push(trim);
    return parts.join(' ');
  }

  /* ---- Modal show / hide ---- */
  function modalEl() { return $('[data-ew-uc-modal]'); }
  /* a11y: element that opened the modal; focus is restored to it on close
     (WCAG 2.4.3). Captured at the moment open() runs. */
  var ucInvoker = null;
  function open(reason) {
    var m = modalEl();
    if (!m) return;
    /* Capture the invoker BEFORE moving focus. */
    ucInvoker = (document.activeElement && typeof document.activeElement.focus === 'function')
      ? document.activeElement
      : null;
    paint(readVehicle());
    m.hidden = false;
    document.body.classList.add('ew-uc-modal-open');
    var first = m.querySelector('.ew-uc-card');
    /* Contain Tab focus inside the dialog and give the first card initial
       focus. trapFocus (global.js) owns the initial .focus() — do NOT also
       call first.focus() here (no double-focus). */
    if (typeof trapFocus === 'function') {
      trapFocus(m, first || m);
    } else if (first) {
      first.focus({ preventScroll: true });
    }
    if (reason) m.setAttribute('data-ew-uc-modal-reason', reason);
  }
  function close() {
    var m = modalEl();
    if (!m) return;
    m.hidden = true;
    document.body.classList.remove('ew-uc-modal-open');
    m.removeAttribute('data-ew-uc-modal-reason');
    m.removeAttribute('data-ew-uc-resolving');
    /* Release the focus trap and restore focus to the invoker. */
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(ucInvoker || null);
    } else if (ucInvoker && typeof ucInvoker.focus === 'function') {
      ucInvoker.focus();
    }
    ucInvoker = null;
  }
  /* capture-v3 — "Change vehicle" affordance inside the modal.
     Close the use-case modal so the vehicle resolver doesn't stack
     on top of it. Mark the modal data-ew-uc-resolving so the
     resolver-resolved handler knows to re-open us with the new
     vehicle. */
  function startVehicleChange() {
    var m = modalEl();
    if (m) m.setAttribute('data-ew-uc-resolving', 'true');
    close();
    /* The vehicle-resolver opens automatically via the click on the
       button (it carries data-ew-vresolver-open) — no extra call here. */
  }

  /* ---- Paint pre-guess + Drift-prominent + latched state on EVERY
         use-case row on the page (inline capture + modal). ---- */
  function paint(v) {
    var vehicle = v || readVehicle();
    /* 3-tier model (LP 2026-05-29): the three cards map 1:1 to the
       three liveries, so pre-guess + latched state key off data-livery
       (the livery id) rather than the use-case key — this keeps the
       right card lit even for a visitor whose persisted use-case is one
       of the legacy six (e.g. 'track' → highlight the Performance card)
       and survives the use-case → livery collapse cleanly. */
    var liveryMap = (window.EWLivery && window.EWLivery.USE_CASE_TO_LIVERY) || {};
    var liveryNow = (window.EWLivery && window.EWLivery.read) ? window.EWLivery.read() : null;

    var preGuessLivery = null;
    var isDriftProminent = false;
    if (vehicle && window.EWLivery && window.EWLivery.preGuessUseCase) {
      var chassis = chassisOf(vehicle);
      var engine  = engineFamily(engineOf(vehicle));
      var preGuess = window.EWLivery.preGuessUseCase(chassis, engine);
      preGuessLivery = liveryMap[preGuess] || null;
      if (window.EWLivery.DRIFT_PROMINENT_CHASSIS &&
          window.EWLivery.DRIFT_PROMINENT_CHASSIS.indexOf(chassis) !== -1) {
        isDriftProminent = true;
      }
    }

    /* Update veh label slots */
    $$('[data-ew-uc-modal-veh]').forEach(function (el) {
      el.textContent = vehicle ? vehLabel(vehicle).toUpperCase() : 'YOUR VEHICLE';
    });
    $$('[data-ew-capture-veh-label]').forEach(function (el) {
      el.textContent = vehicle ? vehLabel(vehicle) : 'your vehicle';
    });

    /* Paint each card across every row — keyed on data-livery */
    $$('[data-ew-use-case-row]').forEach(function (row) {
      $$('.ew-uc-card', row).forEach(function (c) {
        var lv = c.getAttribute('data-livery');
        /* Pre-guess flag — Drift never pre-guessed (spec) */
        if (lv && lv === preGuessLivery && lv !== 'drifter') c.setAttribute('data-pre-guess', 'true');
        else c.removeAttribute('data-pre-guess');
        /* Drift prominence */
        if (lv === 'drifter' && isDriftProminent) c.setAttribute('data-drift-prominent', 'true');
        else c.removeAttribute('data-drift-prominent');
        /* Latched — the card whose livery is the active livery */
        c.setAttribute('aria-checked', (lv && lv === liveryNow) ? 'true' : 'false');
      });
    });
  }

  /* ---- Delegated click handling ---- */
  document.addEventListener('click', function (e) {
    /* "Change vehicle" — start the vehicle-resolver flow from the
       modal (capture-v3). The button also carries
       data-ew-vresolver-open; we just close ourselves so the resolver
       doesn't stack on top, and set a flag so we re-open on resolve. */
    if (e.target.closest('[data-ew-uc-modal-veh-change]')) {
      startVehicleChange();
      /* Do NOT preventDefault — let the resolver open via its own
         delegated handler on the same click. */
      return;
    }
    /* Close the modal (scrim / × close button). An explicit close is a
       dismissal — flag it so we don't auto-re-pop on the next page. */
    if (e.target.closest('[data-ew-uc-modal-close]')) {
      e.preventDefault(); markDismissed(); close(); return;
    }
    /* Use-case card → set via EWLivery. A positive choice is also a
       dismissal of the unsolicited prompt — flag it so we don't re-pop. */
    var card = e.target.closest('.ew-uc-card');
    if (card && card.getAttribute('data-ew-use-case')) {
      var uc = card.getAttribute('data-ew-use-case');
      markDismissed();
      if (window.EWLivery && window.EWLivery.setUseCase) {
        window.EWLivery.setUseCase(uc);
      }
      return;
    }
    /* "Not sure yet — keep it neutral" */
    if (e.target.closest('[data-ew-use-case-clear]')) {
      try { localStorage.removeItem('ew_use_case'); } catch (_) {}
      markDismissed();
      if (window.EWLivery && window.EWLivery.clear) window.EWLivery.clear();
      paint();
      close();
      return;
    }
  });

  /* ---- Auto-open on vehicle-resolved when no use case is set ----
     The homepage capture section handles its OWN inline reveal — when
     the visitor already sits on the capture section we don't double
     up by also opening a modal. Detection: any [data-ew-capture] is
     in the viewport / DOM. */
  /* G-A7 — gate eligibility. The use-case modal is only an unsolicited
     auto-overlay where it's contextually part of a vehicle/tune flow.
     Everywhere else (cart, 404, editorial, utility, /shop hero) it stays
     closed and is reachable on demand via the header override pill
     (EWUseCasePrompt.open). */
  function inContextualFlow() {
    /* The Tune Builder / configurator (/pages/configure*) — the modal is
       a natural part of "Step 1: your vehicle" there. */
    return !!document.querySelector('[data-pf]');
  }
  function shouldAutoOpen() {
    if (!window.EWLivery || !window.EWLivery.readUseCase) return false;
    if (window.EWLivery.readUseCase()) return false; /* already declared */
    if (readDismissed()) return false; /* visitor already dismissed it */
    /* If the inline capture section is on this page, it'll do the
       reveal — don't double-prompt with a modal. */
    if (document.querySelector('[data-ew-capture]')) return false;
    /* Only auto-open as part of the configurator flow — never as a
       floating overlay over general content. */
    if (!inContextualFlow()) return false;
    return true;
  }

  document.addEventListener('ew:vehicle-resolved', function (e) {
    paint(e.detail);
    /* capture-v3 — if the resolve came FROM the use-case modal's
       "Change vehicle" affordance, the modal was closed pre-resolver;
       re-open it so the visitor lands back on the use-case row with
       the new vehicle pre-filled. */
    var m = modalEl();
    if (m && m.getAttribute('data-ew-uc-resolving') === 'true') {
      m.removeAttribute('data-ew-uc-resolving');
      setTimeout(function () { open('after-vehicle-change'); }, 220);
      return;
    }
    /* G-A7 — suppress auto-open for the resolver's BOOT re-emit. The
       vehicle-resolver re-fires `ew:vehicle-resolved` for the persisted
       vehicle on EVERY page load (its boot()); that re-emit is not a
       fresh user action, so it must not pop the overlay sitewide. Only
       resolutions that happen AFTER initial boot (the visitor actually
       picks/changes a vehicle) are eligible. */
    if (!booted) return;
    if (shouldAutoOpen()) {
      /* Tiny delay lets the vehicle-resolver modal finish closing
         first so the use-case modal doesn't stack on top. */
      setTimeout(function () { open('after-vehicle-resolved'); }, 220);
    }
  });

  document.addEventListener('ew:use-case-changed', function () {
    paint();
    close();
  });

  /* ---- Keyboard escape ---- */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var m = modalEl();
    if (m && !m.hidden) { markDismissed(); close(); }
  });

  /* ---- Initial paint when DOM is ready ---- */
  /* `booted` stays false through the synchronous initial-load tick so the
     vehicle-resolver's boot re-emit of the persisted vehicle does NOT
     auto-pop the modal (G-A7). Flipped on the next macrotask, after all
     synchronous boot-time `ew:vehicle-resolved` dispatches have run. */
  var booted = false;
  function boot() {
    paint();
    setTimeout(function () { booted = true; }, 0);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.EWUseCasePrompt = { open: open, close: close, paint: paint };
})();
